import { CommonModule } from '@angular/common';
import { Component, EventEmitter, forwardRef, Input, Output } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-si-form-field',
  imports: [FormsModule, CommonModule, MatInputModule],
  templateUrl: './si-form-field.component.html',
  styleUrl: './si-form-field.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SiFormFieldComponent),
      multi: true
    }
  ]
})
export class SiFormFieldComponent implements ControlValueAccessor {

  private internalValue: string | number = '';
  private percentFocused = false;
  private percentEditValue = '';
  private inputDisabled = false;
  private cvaDisabled = false;
  effectiveDisabled = false;

  private onChange: (value: string | number) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(
    private readonly translateService: TranslateService
  ) {}

  get displayValue(): string | number {
    if (this.type !== 'percent') {
      return this.inputValue;
    }
    if (this.percentFocused) {
      return this.percentEditValue;
    }
    if (this.inputValue === '') {
      return '';
    }
    const language = this.translateService.currentLang || this.translateService.defaultLang || 'en-US';
    return new Intl.NumberFormat(language, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      useGrouping: false
    }).format(Number(this.inputValue));
  }

  private get decimalMarker(): string {
    const language = this.translateService.currentLang || this.translateService.defaultLang || 'en-US';
    return new Intl.NumberFormat(language).formatToParts(1.1)
      .find(part => part.type === 'decimal')?.value ?? '.';
  }

  onFocus(): void {
    if (this.type === 'percent') {
      this.percentEditValue = String(this.inputValue).replace('.', this.decimalMarker);
      this.percentFocused = true;
    }
  }

  @Input()
  set inputValue(value: string | undefined | number) {
    this.internalValue = value ?? '';
  }
  get inputValue(): string | number {
    return this.internalValue;
  }

  @Input()
  set isDisabled(value: boolean) {
    this.inputDisabled = !!value;
    this.syncDisabledState();
  }
  get isDisabled(): boolean {
    return this.inputDisabled;
  }

  @Output() inputValueChange = new EventEmitter<string | number>();
  @Output() blur = new EventEmitter<void>();
  @Input() isReadMode: boolean = true;
  @Input() formControlClass: string = '';
  @Input() type: string = 'text';
  @Input('maxlength') maxLength?: number;
  @Input() max?: number;

  onValueChange(value: string | number): void {
    if (this.type === 'number' || this.type === 'percent') {
      return;
    }

    this.internalValue = value;
    this.onChange(this.internalValue);
    this.inputValueChange.emit(value);
  }

  onNumberInput(event: Event): void {
    if (this.type !== 'number' && this.type !== 'percent') {
      return;
    }

    const input = event.target as HTMLInputElement;

    if (this.type === 'percent') {
      let normalizedValue = input.value.replace(',', '.').replace(/[^\d.\-]/g, '');
      const isNegative = normalizedValue.startsWith('-');
      normalizedValue = normalizedValue.replace(/-/g, '').replace(/\.(?=.*\.)/g, '');
      const [integerPart, decimalPart] = normalizedValue.split('.');
      normalizedValue = decimalPart === undefined ? integerPart : `${integerPart}.${decimalPart.slice(0, 2)}`;
      const numericValue = Number(normalizedValue);
      const nextValue = normalizedValue === '' || !Number.isFinite(numericValue)
        ? '' : Math.min(100, Math.max(0, isNegative ? -numericValue : numericValue));
      if (nextValue !== '' && (isNegative || numericValue > 100)) {
        normalizedValue = String(nextValue);
      }
      this.percentEditValue = normalizedValue.replace('.', this.decimalMarker);
      input.value = this.percentEditValue;
      this.internalValue = nextValue;
      this.onChange(nextValue);
      this.inputValueChange.emit(nextValue);
      return;
    } else {
      input.value = input.value.replace(/\D/g, '').replace(/^0+(?=\d)/, '');
    }

    const count = this.maxLength;
    if (count !== undefined && input.value.length > count) {
      input.value = input.value.slice(0, count);
    }

    if (this.max !== undefined && Number(input.value) > this.max) {
      input.value = this.max.toString();
    }

    const nextValue: string | number = input.value === '' ? '' : Number(input.value);
    this.inputValue = nextValue;
    this.onChange(nextValue);
    this.inputValueChange.emit(nextValue);
  }

  get isInputNumberOrPercent(): boolean {
    return this.type === 'number' || this.type === 'percent';
  }

  onNumberKeyDown(event: KeyboardEvent): void {
    if (this.type !== 'number') {
      return;
    }

    if (event.key === 'e' || event.key === 'E') {
      event.preventDefault();
    }

    if (event.key === '.' || event.key === ',' || event.key === '-' || event.key === '+') {
      event.preventDefault();
    }
  }

  onBlur(): void {
    this.percentFocused = false;
    this.blur.emit();
    this.onTouched();
  }

  writeValue(value: string | number | null): void {
    this.internalValue = value ?? '';
  }

  registerOnChange(fn: (value: string | number) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.cvaDisabled = !!isDisabled;
    this.syncDisabledState();
  }

  private syncDisabledState(): void {
    this.effectiveDisabled = this.inputDisabled || this.cvaDisabled;
  }

}
